import React, { useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Typography,
  Card,
  TextField,
  Button,
  TableSortLabel,
  TablePagination,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Stack,
  IconButton,
  Divider,
  CircularProgress,
  Skeleton
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { debounce } from 'lodash';

import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SecurityIcon from '@mui/icons-material/Security';

import PhoneIcon from '@mui/icons-material/Phone';
import PublicIcon from '@mui/icons-material/Public';
import LockIcon from '@mui/icons-material/Lock';
import WebsiteIcon from '@mui/icons-material/Language';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';

import ErrorNotification from '../../components/ErrorNotification';
import { alpha , darken, lighten} from '@mui/material/styles';
import { BASEURL ,PRIMARYCOLOR,SECONDARYCOLOR,logoURL,COMPANY } from '../../Constants';
import Nav from './nav';


const brandColor = PRIMARYCOLOR;

// === Grade helpers ===
const getGrade = (score) => {
  if (score === null || score === undefined) return '?';
  if (score < 60) return 'F';
  if (score < 70) return 'D';
  if (score < 80) return 'C';
  if (score < 90) return 'B';
  if (score <= 95) return 'A-';
  return 'A+';
};

const getGradeColor = (grade) => {
  switch (grade) {
    case 'A+':
    case 'A-':
      return '#16a34a'; // green
    case 'B':
      return '#2563eb'; // blue
    case 'C':
      return '#f59e0b'; // orange
    case 'D':
      return '#eab308'; // yellow
    case 'F':
      return '#ef4444'; // red
    default:
      return '#9e9e9e'; // grey for '?'
  }
};

const clamp0to100 = (n) =>
  typeof n === 'number' && isFinite(n) ? Math.max(0, Math.min(100, n)) : 0;

// === Type meta (chips/icons/colors) ===
const TYPE_META = {
  vishing: { label: 'Vishing', icon: PhoneIcon, color: 'warning' },
  darkweb: { label: 'Darkweb', icon: LockIcon, color: 'secondary' },
  api: { label: 'API', icon: WebsiteIcon, color: 'info' },
  web: { label: 'Web', icon: WebsiteIcon, color: 'primary' },
  external: { label: 'External', icon: PublicIcon, color: 'success' },
  osint: { label: 'OSINT', icon: TravelExploreIcon, color: 'default' },
};

const typeKeys = ['external', 'web', 'api', 'vishing', 'darkweb', 'osint'];

const OrganizationPentests = () => {
  const navigate = useNavigate();

  const [error, setError] = useState(null);
  const [pentestsData, setPentestsData] = useState([]);
  const [filteredPentests, setFilteredPentests] = useState([]);
  const [sortOrder, setSortOrder] = useState('asc');
  const [sortBy, setSortBy] = useState('name');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTypes, setSelectedTypes] = useState(new Set());
  const [openInfo, setOpenInfo] = useState(false);
  const [selectedPentest, setSelectedPentest] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [loading, setLoading] = useState(true);
  const { id: organizationId } = useParams();

  // --- Data fetch ---
  function getPentests(token) {
    setLoading(true);
    fetch(`${BASEURL}/organization/pentests?organization_id=${organizationId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + token,
      },
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.error) {
          if (data.message === 'Invalid Token') {
            navigate('/');
          } else {
            setError(data.message);
          }
          setPentestsData([]);
          setFilteredPentests([]);
        } else {
          setPentestsData(data.pentests || []);
          setFilteredPentests(data.pentests || []);
          setError(null);
        }
      })
      .catch(() => setError('Error fetching pentests'))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    const token = sessionStorage.getItem('token');
    if (token) getPentests(token);
    else navigate('/');
  }, [navigate]);

  // --- Search + Filter ---
  const debouncedSetSearch = useMemo(
    () => debounce((v) => setSearchQuery(v.toLowerCase()), 300),
    []
  );

  useEffect(() => {
    const q = searchQuery.trim();
    const hasTypeFilter = selectedTypes.size > 0;

    const next = pentestsData.filter((p) => {
      const matchesQuery = !q || (p.name || '').toLowerCase().includes(q);
      const matchesType = !hasTypeFilter || selectedTypes.has((p.type || '').toLowerCase());
      return matchesQuery && matchesType;
    });

    setFilteredPentests(next);
    setPage(0);
  }, [searchQuery, selectedTypes, pentestsData]);

  // --- Sorting ---
  const handleSort = (property) => {
    const isAsc = sortBy === property && sortOrder === 'asc';
    setSortOrder(isAsc ? 'desc' : 'asc');
    setSortBy(property);
  };

  const sortedPentests = useMemo(() => {
    const arr = [...filteredPentests];
    arr.sort((a, b) => {
      const av = a?.[sortBy];
      const bv = b?.[sortBy];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;

      if (typeof av === 'string' && typeof bv === 'string') {
        return sortOrder === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      }
      if (av < bv) return sortOrder === 'asc' ? -1 : 1;
      if (av > bv) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
    return arr;
  }, [filteredPentests, sortBy, sortOrder]);

  // --- Info + Delete dialogs ---
  const handleOpenInfo = (pentest) => {
    setSelectedPentest(pentest);
    setOpenInfo(true);
  };
  const handleCloseInfo = () => {
    setOpenInfo(false);
    setSelectedPentest(null);
  };
  const handleDeletePentest = (pentest) => {
    setSelectedPentest(pentest);
    setOpenDeleteDialog(true);
  };
  const handleConfirmDelete = () => {
    const token = sessionStorage.getItem('token');
    fetch(`${BASEURL}/pentests?pentest_id=${selectedPentest.id}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + token,
      },
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.success) {
          setFilteredPentests((prev) => prev.filter((p) => p.id !== selectedPentest.id));
          setPentestsData((prev) => prev.filter((p) => p.id !== selectedPentest.id));
          setError(null);
        } else {
          setError(data.message || 'Failed to delete pentest');
        }
      })
      .catch(() => setError('Failed to delete pentest'))
      .finally(() => {
        setOpenDeleteDialog(false);
        setSelectedPentest(null);
      });
  };

  // --- UI helpers ---
  const toggleType = (key) => {
    setSelectedTypes((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const showNoPentestMessage = !loading && filteredPentests.length === 0;

  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: '100vh',
        bgcolor: (t) => alpha(t.palette.grey[100], 0.8),
        backgroundImage: `radial-gradient(${alpha('#cfe8ff', 0.5)} 1px, transparent 1px)`,
        backgroundSize: '16px 16px',
      }}
    >
      <ErrorNotification errorMessage={error} />
      <Box sx={{ flexGrow: 1, p: 3 }}>
        {/* ===== YOUR TOP NAV GOES HERE ===== */}
        <Nav />
        {/* Controls */}
        <Card sx={{ p: 2.5, borderRadius: 3, mb: 3 }}>
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <Box sx={{ flex: 1, position: 'relative' }}>
                <TextField
                  fullWidth
                  placeholder="Search pentests by name..."
                  InputProps={{
                    startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                    endAdornment: searchQuery ? (
                      <IconButton
                        size="small"
                        onClick={() => {
                          setSearchQuery('');
                        }}
                      >
                        <ClearIcon />
                      </IconButton>
                    ) : null,
                  }}
                  onChange={(e) => debouncedSetSearch(e.target.value)}
                />
              </Box>

              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                {typeKeys.map((k) => {
                  const meta = TYPE_META[k];
                  const Icon = meta.icon;
                  const selected = selectedTypes.has(k);
                  return (
                    <Chip
                      key={k}
                      icon={<Icon />}
                      label={meta.label}
                      clickable
                      color={selected ? meta.color : 'default'}
                      onClick={() => toggleType(k)}
                      sx={{
                        borderRadius: 2,
                        fontWeight: 600,
                        ...(selected
                          ? {}
                          : {
                              bgcolor: (t) => alpha(t.palette.text.primary, 0.06),
                            }),
                      }}
                    />
                  );
                })}
              </Stack>
            </Stack>
          </Stack>
        </Card>

        {/* Table / Empty / Loading */}
        <Card sx={{ p: 0, borderRadius: 3, overflow: 'hidden' }}>
          {loading ? (
            <Box sx={{ p: 3 }}>
              {[...Array(6)].map((_, i) => (
                <Skeleton key={i} variant="rounded" height={56} sx={{ mb: 1 }} />
              ))}
            </Box>
          ) : showNoPentestMessage ? (
            <Box
              sx={{
                textAlign: 'center',
                py: 8,
                px: 2,
                border: (t) => `2px dashed ${alpha(t.palette.divider, 0.8)}`,
                borderRadius: 3,
                m: 3,
                bgcolor: (t) => alpha(t.palette.background.default, 0.6),
              }}
            >
              <SecurityIcon sx={{ fontSize: 56, color: brandColor, mb: 1 }} />
              <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>
                No Pentests Yet
              </Typography>
              <Typography sx={{ mb: 2, color: 'text.secondary' }}>
                Kick off your first assessment in under a minute.
              </Typography>
              <Button
                variant="contained"
                onClick={() => navigate('/pentests/create')}
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700 }}
              >
                Create Your First Pentest
              </Button>
            </Box>
          ) : (
            <>
              <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow
                      sx={{
                        '& th': {
                          background: (t) =>
                            `linear-gradient(180deg, ${alpha(brandColor, 0.95)}, ${alpha(brandColor, 0.9)})`,
                          color: 'white',
                          fontWeight: 800,
                          borderBottom: 'none',
                        },
                      }}
                    >
                      <TableCell width={100}>Grade</TableCell>

                      <TableCell sortDirection={sortBy === 'type' ? sortOrder : false}>
                        <TableSortLabel
                          active={sortBy === 'type'}
                          direction={sortBy === 'type' ? sortOrder : 'asc'}
                          onClick={() => handleSort('type')}
                          sx={{ color: 'white',
                            '& .MuiTableSortLabel-icon': { color: 'inherit !important' }
                          }}
                        >
                          Type
                        </TableSortLabel>
                      </TableCell>

                      <TableCell sortDirection={sortBy === 'name' ? sortOrder : false}>
                        <TableSortLabel
                          active={sortBy === 'name'}
                          direction={sortBy === 'name' ? sortOrder : 'asc'}
                          onClick={() => handleSort('name')}
                          sx={{ color: 'white',
                            '& .MuiTableSortLabel-icon': { color: 'inherit !important' }
                          }}
                        >
                          Name
                        </TableSortLabel>
                      </TableCell>

                      <TableCell align="right" sortDirection={sortBy === 'assets_count' ? sortOrder : false}>
                        <TableSortLabel
                          active={sortBy === 'assets_count'}
                          direction={sortBy === 'assets_count' ? sortOrder : 'asc'}
                          onClick={() => handleSort('assets_count')}
                          sx={{ color: 'white',
                            '& .MuiTableSortLabel-icon': { color: 'inherit !important' }
                          }}
                        >
                          <Tooltip title="Assets, Users" arrow>
                            Targets
                          </Tooltip>
                        </TableSortLabel>
                      </TableCell>

                      <TableCell align="right" sortDirection={sortBy === 'exploits_count' ? sortOrder : false}>
                        <TableSortLabel
                          active={sortBy === 'exploits_count'}
                          direction={sortBy === 'exploits_count' ? sortOrder : 'asc'}
                          onClick={() => handleSort('exploits_count')}
                          sx={{ color: 'white',
                            '& .MuiTableSortLabel-icon': { color: 'inherit !important' }
                          }}
                        >
                          <Tooltip title="Exploits, Phished Credentials" arrow>
                            Findings
                          </Tooltip>
                        </TableSortLabel>
                      </TableCell>

                      <TableCell align="center" width={160}>
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody
                    sx={{
                      '& tr:nth-of-type(odd)': { backgroundColor: (t) => alpha(t.palette.grey[50], 0.8) },
                      '& tr:hover': { backgroundColor: (t) => alpha(t.palette.primary.light, 0.08) },
                    }}
                  >
                    {sortedPentests
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((row) => {
                        const grade = getGrade(row.risk_score);
                        const gradeColor = getGradeColor(grade);
                        const pct = clamp0to100(row.risk_score);
                        const tKey = (row.type || 'external').toLowerCase().includes('osint') ? 'osint' : (row.type || 'external').toLowerCase();
                        const meta = TYPE_META[tKey] || TYPE_META.external;
                        const Icon = meta.icon;
                        const isOsint = tKey === 'osint';

                        return (
                          <TableRow key={row.id || row.name} hover>
                            {/* Grade: progress ring + letter */}
                            <TableCell>
                              {isOsint ? (
                                <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                                  <CircularProgress variant="determinate" value={100} size={40} thickness={5} sx={{ color: alpha('#64748b', 0.28) }} />
                                  <Box sx={{ inset: 0, position: 'absolute', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Avatar
                                      sx={{
                                        width: 28,
                                        height: 28,
                                        fontSize: 11,
                                        bgcolor: alpha('#64748b', 0.14),
                                        color: '#64748b',
                                        border: `1px solid ${alpha('#64748b', 0.22)}`,
                                        fontWeight: 950,
                                      }}
                                    >
                                      N/A
                                    </Avatar>
                                  </Box>
                                </Box>
                              ) : (
                                <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                                  <CircularProgress
                                    variant="determinate"
                                    value={pct}
                                    size={40}
                                    thickness={5}
                                    sx={{ color: gradeColor }}
                                  />
                                  <Box
                                    sx={{
                                      top: 0,
                                      left: 0,
                                      bottom: 0,
                                      right: 0,
                                      position: 'absolute',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                    }}
                                  >
                                    <Avatar
                                      sx={{
                                        width: 28,
                                        height: 28,
                                        fontSize: 12,
                                        bgcolor: gradeColor,
                                        color: 'white',
                                        fontWeight: 800,
                                      }}
                                    >
                                      {grade}
                                    </Avatar>
                                  </Box>
                                </Box>
                              )}
                            </TableCell>

                            {/* Type chip */}
                            <TableCell>
                              <Chip
                                icon={<Icon />}
                                label={meta.label}
                                size="small"
                                color={meta.color}
                                sx={{ borderRadius: 2, fontWeight: 700 }}
                              />
                            </TableCell>

                            {/* Name clickable */}
                            <TableCell sx={{ fontWeight: 700 }}>
                              <Tooltip title="View details" arrow>
                                <Box
                                  component="span"
                                  sx={{
                                    cursor: 'pointer',
                                    '&:hover': { textDecoration: 'underline' },
                                  }}
                                  onClick={() => handleOpenInfo(row)}
                                >
                                  {row.name}
                                </Box>
                              </Tooltip>
                            </TableCell>

                            {/* Targets & Findings */}
                            <TableCell align="right">
                              <Chip
                                label={row.assets_count ?? '0'}
                                size="small"
                                sx={{
                                  fontWeight: 700,
                                  borderRadius: 2,
                                  bgcolor: (t) => alpha(t.palette.success.main, 0.08),
                                  color: (t) => t.palette.success.main,
                                }}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Chip
                                label={row.exploits_count ?? '0'}
                                size="small"
                                sx={{
                                  fontWeight: 700,
                                  borderRadius: 2,
                                  bgcolor: (t) => alpha(t.palette.error.main, 0.08),
                                  color: (t) => t.palette.error.main,
                                }}
                              />
                            </TableCell>

                            {/* Actions */}
                            <TableCell align="center">
                              <Stack direction="row" spacing={1} justifyContent="center">
                                <Tooltip title="Open dashboard" arrow>
                                  <IconButton
                                    color="primary"
                                    onClick={() => {
                                      if (row.type === 'vishing') {
                                        navigate(`/pentest/vishing/dashboard/${row.id}`);
                                      } else if (row.type === 'darkweb') {
                                        navigate(`/pentest/darkweb/dashboard/${row.id}`);
                                      } else if (row.type === 'api') {
                                        navigate(`/pentest/api/dashboard/${row.id}`);
                                      } else if (row.type === 'web') {
                                        navigate(`/pentest/web/dashboard/${row.id}`); // preserve existing route
                                      } else {
                                        navigate(`/pentest/dashboard/${row.id}`);
                                      }
                                    }}
                                  >
                                    <OpenInNewIcon />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="More info" arrow>
                                  <IconButton color="info" onClick={() => handleOpenInfo(row)}>
                                    <InfoOutlinedIcon />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete pentest" arrow>
                                  <IconButton color="error" onClick={() => handleDeletePentest(row)}>
                                    <DeleteOutlineIcon />
                                  </IconButton>
                                </Tooltip>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={sortedPentests.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                sx={{
                  px: 2,
                  '& .MuiTablePagination-toolbar': { py: 1.5 },
                }}
              />
            </>
          )}
        </Card>
      </Box>

      {/* Info dialog */}
      <Dialog open={openInfo} onClose={handleCloseInfo} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ p: 0 }}>
          {selectedPentest && (
            <Box
              sx={{
                p: 3,
                background: `linear-gradient(135deg, ${brandColor}, #003a88)`,
                color: 'white',
              }}
            >
              <Stack direction="row" spacing={2} alignItems="center">
                <Avatar sx={{ bgcolor: alpha('#fff', 0.2) }}>
  {React.createElement(
    TYPE_META[(selectedPentest?.type || 'external').toLowerCase()]?.icon || PublicIcon,
    { fontSize: 'small' }
  )}
</Avatar>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.1 }}>
                    {selectedPentest.name}
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                    <Chip
                      size="small"
                      label={`Type: ${TYPE_META[(selectedPentest.type || 'external').toLowerCase()]?.label ?? 'External'}`}
                      sx={{ bgcolor: alpha('#fff', 0.15), color: '#fff' }}
                    />
                    <Chip
                      size="small"
                      label={`Targets: ${selectedPentest.assets_count ?? 0}`}
                      sx={{ bgcolor: alpha('#fff', 0.15), color: '#fff' }}
                    />
                    <Chip
                      size="small"
                      label={`Findings: ${selectedPentest.exploits_count ?? 0}`}
                      sx={{ bgcolor: alpha('#fff', 0.15), color: '#fff' }}
                    />
                  </Stack>
                </Box>
              </Stack>
            </Box>
          )}
        </DialogTitle>
        <DialogContent dividers>
          {selectedPentest && (
            <Stack spacing={2}>
              <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
                Summary
              </Typography>
              <Typography>
                {selectedPentest.details || 'No additional details provided.'}
              </Typography>

              <Divider />

              <Stack direction="row" spacing={2}>
                <Box>
                  <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
                    Risk Score
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                    <Avatar
                      sx={{
                        bgcolor: getGradeColor(getGrade(selectedPentest.risk_score)),
                        color: 'white',
                        fontWeight: 800,
                        width: 36,
                        height: 36,
                      }}
                    >
                      {getGrade(selectedPentest.risk_score)}
                    </Avatar>
                    <Typography fontWeight={700}>
                      {clamp0to100(selectedPentest.risk_score)} / 100
                    </Typography>
                  </Stack>
                </Box>
              </Stack>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseInfo} sx={{ textTransform: 'none' }}>
            Close
          </Button>
          {selectedPentest && (
            <Button
              variant="contained"
              onClick={() => {
                handleCloseInfo();
                const row = selectedPentest;
                if (row.type === 'vishing') {
                  navigate(`/pentest/vishing/dashboard/${row.id}`);
                } else if (row.type === 'darkweb') {
                  navigate(`/pentest/darkweb/dashboard/${row.id}`);
                } else if (row.type === 'api') {
                  navigate(`/pentest/api/dashboard/${row.id}`);
                } else if (row.type === 'web') {
                  navigate(`/pentest/api/dashboard/${row.id}`);
                } else {
                  navigate(`/pentest/dashboard/${row.id}`);
                }
              }}
              sx={{ textTransform: 'none', borderRadius: 2 }}
            >
              Open Dashboard
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent dividers>
          <Typography>
            Are you sure you want to delete{' '}
            <strong>{selectedPentest?.name ?? 'this pentest'}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained" sx={{ textTransform: 'none' }}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OrganizationPentests;
